import { cn } from "../lib/utils.js";

/**
 * A labelled input.
 *
 * Every field on the account screens goes through here so that the label is
 * always bound to the input by `htmlFor`/`id` — which is what makes these
 * screens usable with a screen reader, and what lets a test find a field by the
 * words next to it rather than by a CSS class.
 */
export function Field({
  id,
  label,
  type = "text",
  value,
  onChange,
  hint,
  autoComplete,
  autoCapitalize,
  inputMode,
  maxLength,
  minLength,
  required,
  placeholder,
  className,
}: {
  id: string;
  label: string;
  type?: "text" | "email" | "password";
  value: string;
  onChange: (value: string) => void;
  /** Shown under the field — what a password needs, what a code looks like. */
  hint?: string;
  autoComplete?: string;
  autoCapitalize?: "none" | "words" | "characters";
  inputMode?: "text" | "email";
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  placeholder?: string;
  /** Extra classes on the input — the party code box is typed from across a room. */
  className?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm text-white/70" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        inputMode={inputMode}
        maxLength={maxLength}
        minLength={minLength}
        required={required}
        placeholder={placeholder}
        // The focus state is a ring *and* a colour change, not colour alone —
        // the same rule the board follows, and the reason it survives being
        // driven by a D-pad or read by somebody who cannot separate the hues.
        // `w-full` is not decoration: an input's own width is twenty characters
        // (see `styles/index.css`), so a field dropped anywhere that is not a
        // stretching flex column would otherwise size itself from its font
        // rather than from the screen it is on.
        className={cn(
          "min-h-14 w-full rounded-lg border border-white/15 bg-stage-900 px-4 text-lg outline-none transition placeholder:text-white/25 focus:border-flash-500 focus:ring-2 focus:ring-flash-500/35",
          className,
        )}
      />
      {hint && <p className="text-xs text-white/40">{hint}</p>}
    </div>
  );
}
